/**
 * 模型管理 · 模型同步：把数据中台（NDP）的模型发版同步到平台模型管理。
 *
 * 新主版本 = 基版（默认最新正式主版本，可改）+ 中台改动（design §11）。
 * 流程（openspec ndp-model-sync-task）：输入发版号、选基版 → 预览（平台内检查，不建任务）→ 勾选可同步的
 * 发版号确认 → 一个主任务、每个发版号一个子项；平台调度 BOS 抓取（FetchObject）把中台文件搬到业务桶，
 * 全部到齐后逐个入库并生成主版本。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, App, Button, Checkbox, DatePicker, Descriptions, Drawer, Empty, Input, Modal, Popconfirm, Progress, Select,
  Space, Steps, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CloudSyncOutlined, EyeOutlined, FileTextOutlined, PlusOutlined, ReloadOutlined, RedoOutlined,
  SearchOutlined, StopOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  cancelSyncTask, createSyncTask, getSyncItemFiles, getSyncTask, listNdpReleases, listSyncTasks, NDP_RELEASE_LIMIT, previewSync,
  type NdpRelease, type PreviewItem, type PreviewResponse, type Reason, type SyncFile, type SyncItem, type SyncSummary,
  type SyncTask, type SyncTaskDetail, type TaskListQuery,
} from '@apis/modelSync';
import { listModelNames, listReleaseTree } from '@apis/model';
import {
  FILE_STATUS, ITEM_STATUS, NO_BASE, PREVIEW_MAX_VERSIONS, STAGE_LABEL, TASK_STATUS, VERDICT, buildRemovals, buildSkipModels,
  canSync, formatBytes, parseVersionsInput, progressPercent, reasonLabel, releaseNameErrors, skipModelErrors,
  syncableModels, versionChangeText, baseParams, onlyUnchangedLeft, retryBaseValue, skipKeepText,
} from '@/utils/modelSync';
import './ModelSync.less';

function errText(e: unknown): string {
  const any = e as { response?: { data?: { message?: string; detail?: unknown } }; message?: string };
  const d = any?.response?.data;
  if (d?.message) {
    return d.message;
  }
  if (d?.detail) {
    return typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail);
  }
  return any?.message ?? String(e);
}

const { Text, Paragraph } = Typography;
const REFRESH_MS = 10_000;
const TASK_ACTIVE = new Set(['pending', 'running']);

const fmtTime = (t: string | null | undefined) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : '-');
function duration(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return '-';
  const sec = Math.max(0, dayjs(end || undefined).diff(dayjs(start), 'second'));
  if (sec < 60) return `${sec} 秒`;
  if (sec < 3600) return `${Math.floor(sec / 60)} 分 ${sec % 60} 秒`;
  return `${Math.floor(sec / 3600)} 小时 ${Math.floor((sec % 3600) / 60)} 分`;
}

function StatusTag({ map, status }: { map: typeof TASK_STATUS; status: string }) {
  const s = map[status] ?? { label: status, tone: 'default' as const };
  return <Tag color={s.tone}>{s.label}</Tag>;
}

function VersionTags({ versions }: { versions: string[] }) {
  if (versions.length <= 2) return <>{versions.map((v) => <Tag key={v}>{v}</Tag>)}</>;
  return (
    <Tooltip title={versions.join('\n')} overlayStyle={{ whiteSpace: 'pre-line' }}>
      <span><Tag>{versions[0]}</Tag><Tag>+{versions.length - 1}</Tag></span>
    </Tooltip>
  );
}

/** detail 是字符串数组 / 对象数组时逐行展示（空壳 onnx、名称占用、兼容性预检错误等）。 */
const DETAIL_FOLD = 5;

function DetailList({ detail }: { detail: unknown }) {
  const [all, setAll] = useState(false);
  const rows = Array.isArray(detail) ? detail : detail && typeof detail === 'object' && 'items' in (detail as object)
    ? ((detail as { items: unknown }).items as unknown[]) : null;
  if (!rows || rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, DETAIL_FOLD);
  return (
    <ul className="msync-detail">
      {shown.map((r, i) => (
        <li key={i}>{typeof r === 'string' ? r : <code>{JSON.stringify(r)}</code>}</li>
      ))}
      {rows.length > DETAIL_FOLD && (
        <li className="msync-detail-more">
          <Button type="link" size="small" onClick={() => setAll((v) => !v)}>
            {all ? '收起' : `展开其余 ${rows.length - DETAIL_FOLD} 条`}
          </Button>
        </li>
      )}
    </ul>
  );
}

function ReasonView({ reason }: { reason: Reason }) {
  return (
    <div className="msync-reason">
      <div>
        {reason.model && <Tag color="orange">{reason.model}</Tag>}
        <Tag color="error">{reasonLabel(reason.code)}</Tag>{reason.message}
      </div>
      <DetailList detail={reason.detail} />
    </div>
  );
}

/**
 * 变化摘要。removal 传入时（新建弹窗、可同步的发版号）「从基版移除」列表可勾选；不传则只读展示已定的移除。
 * skip 传入时模型表多一列「同步」：可同步的默认勾上、可勾掉；不可同步的不勾且不能勾（2026-11-02）。
 */
function SummaryView({ summary, removal, skip }: {
  summary: SyncSummary | null | undefined;
  removal?: { picked: string[]; onChange: (names: string[]) => void };
  skip?: { picked: string[]; onChange: (names: string[]) => void; error?: string };
}) {
  if (!summary) return null;
  const keep = skipKeepText(summary.base_release);
  const isSkipped = (m: SyncSummary['models'][number]) => !!m.skipped || !!skip?.picked.includes(m.name);
  const syncColumn = skip ? [{
    // 勾选列不随「跳过」行淡化，保证取消勾选后还能看清、重新勾上
    title: '同步', width: 56, align: 'center' as const, className: 'msync-sync-col',
    render: (_: unknown, m: SyncSummary['models'][number]) => (
      <Tooltip title={m.blocked ? `不可同步，${keep}` : `取消勾选 = 本次不同步该模型类别，${keep}`}>
        <Checkbox
          checked={!isSkipped(m)} disabled={!!m.blocked}
          onChange={(e) => skip.onChange(e.target.checked
            ? skip.picked.filter((n) => n !== m.name)
            : [...new Set([...skip.picked, m.name])])}
        />
      </Tooltip>
    ),
  }] : [];
  const removable = summary.removable ?? [];
  const removals = summary.removals ?? [];
  return (
    <div className="msync-summary">
      <Descriptions size="small" column={2} items={[
        { key: 'r', label: '将生成主版本', children: summary.new_release_name ? <Text code copyable>{summary.new_release_name}</Text> : '-' },
        { key: 'b', label: '基版', children: summary.base_release ? <Text code>{summary.base_release}</Text> : '无（从空清单开始）' },
        { key: 'e', label: '同步 engine', children: (summary.engine_models ?? []).length > 0 ? (summary.engine_models ?? []).join('、') : '不同步' },
        { key: 'c', label: '变化', children: summary.change_summary ?? '-' },
        ...(summary.skip_summary ? [{ key: 's', label: '不同步', children: <Text type="warning">{summary.skip_summary}</Text> }] : []),
        { key: 'f', label: '需从中台搬运', children: summary.files_to_transfer > 0 ? `${summary.files_to_transfer} 个文件，约 ${formatBytes(summary.bytes_to_transfer)}（其余文件平台已有，直接复用）` : '无（文件平台均已有）' },
        ...(summary.identical_release ? [{ key: 'i', label: '相同主版本', children: <Text>同大版本下已有清单完全相同的 <Text code>{summary.identical_release}</Text></Text> }] : []),
        ...((summary.new_names ?? []).length > 0 ? [{
          key: 'n', label: '新登记名称',
          children: <Space size={4} wrap>{summary.new_names.map((n) => <Tag key={n} color="gold">{n}</Tag>)}<Text type="secondary">（目录未登记，按自动名登记）</Text></Space>,
        }] : []),
      ]} />
      {skip && skip.picked.length > 0 && (
        <Text type="warning" style={{ display: 'block', marginBottom: 4 }}>
          已取消勾选：{skip.picked.join('、')}。这些模型类别不会同步、不搬运文件，{keep}；
          上面的「变化」「需从中台搬运」按预览时全部同步计算，确认后以实际为准。
        </Text>
      )}
      {skip && onlyUnchangedLeft(summary.models, skip.picked) && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 6 }}
          message="剩下要同步的模型类别都和基版一样，不会生成新主版本"
          description="有改动的模型类别都被取消勾选了；确认后该发版入库时会判为「无需同步」。如需生成新主版本，请至少勾选一个有改动的模型类别。"
        />
      )}
      <Table<SyncSummary['models'][number]>
        size="small" pagination={false} rowKey="name" dataSource={summary.models}
        tableLayout="fixed" className="msync-wrap"
        rowClassName={(m) => (isSkipped(m) ? 'msync-row-skipped' : '')}
        columns={[
          ...syncColumn,
          {
            title: '平台模型', dataIndex: 'name', width: 170,
            render: (v, m) => (
              <>
                {v}
                {m.auto_named && <Tag color="gold" style={{ marginLeft: 6 }}>新登记</Tag>}
                {m.blocked && <Tag color="error" style={{ marginLeft: 6 }}>不可同步</Tag>}
                {!skip && m.skipped && !m.blocked && <Tag style={{ marginLeft: 6 }}>未同步</Tag>}
              </>
            ),
          },
          {
            // 中台模型类别（与中台页面上的模型类型名一致）；跳过的模型类别取其中台记录的类型
            title: '中台模型类别', width: 150,
            render: (_, m) => {
              const labels = [...new Set([m.type_label, ...m.records.map((x) => x.type_label)].filter(Boolean))];
              return labels.length > 0 ? labels.join('、') : <Text type="secondary">—</Text>;
            },
          },
          {
            title: '基版版本 → 中台版本', width: 260,
            render: (_, m) => {
              if (isSkipped(m)) {
                return <Text type="secondary">{versionChangeText({ ...m, skipped: true })}</Text>;
              }
              return m.unchanged
                ? <Text type="secondary">不变（{m.version}）</Text>
                : <Text>{versionChangeText(m)}</Text>;
            },
          },
          { title: '安装目录', dataIndex: 'install_dir', render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
          {
            title: <Tooltip title="该模型的文件中，平台没有、需要新写入的 / 平台已有同内容、直接复用的">文件 新写 / 复用</Tooltip>,
            width: 110, render: (_, m) => `${m.upload.length} / ${m.reuse.length}`,
          },
          {
            title: '中台记录', dataIndex: 'records',
            render: (rs: SyncSummary['models'][number]['records'], m) => (
              <>
                {rs.map((x) => <div key={x.name}>{x.name}</div>)}
                {m.note && <Text type="secondary" style={{ fontSize: 12 }}>{m.note}</Text>}
                {(m.skip_reasons ?? []).map((w, i) => <div key={i}><Text type="danger" style={{ fontSize: 12 }}>{w}</Text></div>)}
              </>
            ),
          },
        ]}
      />
      {skip?.error && <Text type="danger">{skip.error}</Text>}
      {removal && removable.length > 0 && (
        <div className="msync-removable" style={{ marginTop: 8 }}>
          <Text strong>基版里有、本次中台发版没有的模型</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>默认保留在新主版本中；勾选的将从新主版本移除</Text>
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', marginTop: 4 }}
            value={removal.picked} onChange={(v) => removal.onChange(v as string[])}
            options={removable.map((r) => ({ value: r.name, label: <>从基版移除 <Text code>{r.name}</Text>{r.version && <Text type="secondary">（{r.version}）</Text>}</> }))}
          />
        </div>
      )}
      {!removal && (removable.length > 0 || removals.length > 0) && (
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          {removals.length > 0 && <div>从基版移除：{removals.join('、')}</div>}
          {removable.filter((r) => !removals.includes(r.name)).length > 0 && (
            <div>保留基版里中台本次没有的：{removable.filter((r) => !removals.includes(r.name)).map((r) => r.name).join('、')}</div>
          )}
        </Paragraph>
      )}
    </div>
  );
}

/* ================================================================== *
 * 新建同步弹窗：输入 → 预览 → 确认
 * ================================================================== */

function CreateSyncModal({ open, initialVersions, retryOf, initialBase, initialEngineModels, onClose, onCreated }: {
  open: boolean; initialVersions: string[]; retryOf?: number;
  /** 重试时沿用的基版下拉框值（含 NO_BASE）；undefined = 默认最新正式主版本 */
  initialBase?: string;
  /** 重试时沿用的 engine 同步模型类别 */
  initialEngineModels?: string[];
  onClose: () => void; onCreated: (task: SyncTaskDetail) => void;
}) {
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [releases, setReleases] = useState<NdpRelease[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  /** 基版主版本；空 = 让后端用最新正式主版本 */
  const [base, setBase] = useState<string | null>(null);
  const [baseOptions, setBaseOptions] = useState<{ value: string; label: string }[]>([]);
  const [baseLoading, setBaseLoading] = useState(false);
  /** 开启 engine 同步的模型类别（默认都不开启） */
  const [engineModels, setEngineModels] = useState<string[]>([]);
  const [engineOptions, setEngineOptions] = useState<{ value: string; label: string }[]>([]);
  /** {发版号: 主版本号}：预览后默认填自动生成的名字，可改 */
  const [releaseNames, setReleaseNames] = useState<Record<string, string>>({});
  /** {发版号: [勾选从基版移除的模型类别]} */
  const [removalPicks, setRemovalPicks] = useState<Record<string, string[]>>({});
  /** {发版号: [勾掉、本次不同步的模型类别]}（不可同步的由后端固定跳过，不在这里） */
  const [skipPicks, setSkipPicks] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setText(initialVersions.join('\n'));
    setReleaseKw('');
    setName('');
    setPreview(null);
    setSelected([]);
    setRemovalPicks({});
    setSkipPicks({});
    setEngineModels(initialEngineModels ?? []);
  }, [open, initialVersions, initialEngineModels]);

  // engine 同步的可选模型类别：已登记、启用的模型类别
  useEffect(() => {
    if (!open) return;
    listModelNames({ include_disabled: false })
      .then((ns) => setEngineOptions(ns.map((n) => ({ value: n.name, label: n.name }))))
      .catch((e) => console.error('加载模型类别失败', e));
  }, [open]);

  // 基版：默认「不使用基版」（2026-11-03：激光检测目录按中台模型类型拆开后，基版里按目录合并同步的旧版本
  // 会与新模型类别同名文件冲突；不带基版从根上避开）。重试沿用失败子项当时的选择；可从主版本树改选基版。
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setBase(initialBase !== undefined ? initialBase : NO_BASE);
    setBaseLoading(true);
    void (async () => {
      try {
        const tree = await listReleaseTree();
        if (!alive) return;
        const opts = (tree?.groups ?? []).flatMap((g) => g.releases.map((r) => ({
          value: r.name, label: `${r.name}（${r.kind === 'dev' ? '开发' : '正式'} · ${g.major_group}）`,
        })));
        setBaseOptions([
          { value: NO_BASE, label: '不使用基版（新主版本只包含中台本次同步的模型）' },
          ...opts,
        ]);
      } catch (e) {
        // 请求层已提示；默认「不使用基版」不依赖主版本列表
        console.error('加载基版主版本失败', e);
      } finally {
        if (alive) setBaseLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, initialBase]);

  const parsed = useMemo(() => parseVersionsInput(text), [text]);

  const [releaseKw, setReleaseKw] = useState('');
  const [releasesError, setReleasesError] = useState<string | null>(null);
  const releaseSeq = useRef(0);
  const searchReleases = useCallback(async (keyword: string) => {
    const seq = ++releaseSeq.current;
    setReleasesLoading(true);
    setReleasesError(null);
    try {
      const rows = await listNdpReleases(keyword || undefined);
      if (seq === releaseSeq.current) {
        setReleases(rows);
      }
    } catch (e) {
      console.error('读取数据中台发版列表失败', e);
      if (seq === releaseSeq.current) {
        setReleases([]);
        setReleasesError(errText(e));
      }
    } finally {
      if (seq === releaseSeq.current) {
        setReleasesLoading(false);
      }
    }
  }, []);
  // 输入停顿 300ms 再查：每次查询后端都要从中台拉全量发版列表
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const t = window.setTimeout(() => void searchReleases(releaseKw.trim()), releaseKw ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [open, releaseKw, searchReleases]);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const bp = baseParams(base);
      const pv = await previewSync(parsed.versions, bp.base, engineModels, bp.noBase);
      setPreview(pv);
      setSelected(pv.items.filter((i) => canSync(i.verdict)).map((i) => i.ndpVersion));
      setReleaseNames(Object.fromEntries(pv.items
        .filter((i) => canSync(i.verdict) && i.summary?.new_release_name)
        .map((i) => [i.ndpVersion, i.summary!.new_release_name as string])));
      setRemovalPicks({});
      setSkipPicks({});
      setStep(1);
    } finally {
      setPreviewing(false);
    }
  };

  const nameErrors = useMemo(() => releaseNameErrors(selected, releaseNames), [selected, releaseNames]);
  const syncableOf = useCallback((v: string) => syncableModels(
    preview?.items.find((i) => i.ndpVersion === v)?.summary?.models), [preview]);
  const skipErrors = useMemo(() => skipModelErrors(skipPicks, selected, syncableOf), [skipPicks, selected, syncableOf]);

  const doCreate = async () => {
    if (!preview || selected.length === 0) return;
    setCreating(true);
    try {
      const task = await createSyncTask({
        previewId: preview.previewId, ndpVersions: selected, name: name.trim() || undefined, retryOf,
        removals: buildRemovals(removalPicks, selected, (v) => (
          preview.items.find((i) => i.ndpVersion === v)?.summary?.removable ?? []).map((r) => r.name)),
        releaseNames: Object.fromEntries(selected.map((v) => [v, (releaseNames[v] ?? '').trim()])),
        skipModels: buildSkipModels(skipPicks, selected, syncableOf),
      });
      message.success(`已创建同步任务 #${task.id}，共 ${task.totalItems} 个发版号`);
      onCreated(task);
    } finally {
      setCreating(false);
    }
  };

  const counts = useMemo(() => {
    const c = { syncable: 0, partial: 0, blocked: 0, already_synced: 0 };
    preview?.items.forEach((i) => { c[i.verdict] += 1; });
    return c;
  }, [preview]);

  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: '发版号', dataIndex: 'ndpVersion', width: 220,
      render: (v, r) => (
        <div>
          <div><Text strong>{v}</Text>{r.releaseKind && <Tag style={{ marginLeft: 6 }} color={r.releaseKind === 'dev' ? 'orange' : 'blue'}>{r.releaseKind}</Tag>}</div>
          {r.ndpVersionName && <Text type="secondary" style={{ fontSize: 12 }}>{r.ndpVersionName}</Text>}
        </div>
      ),
    },
    { title: '结论', dataIndex: 'verdict', width: 100, render: (v) => <StatusTag map={VERDICT} status={v} /> },
    {
      title: '原因 / 警告',
      render: (_, r) => (
        <div>
          {r.reasons.map((reason, i) => <ReasonView key={i} reason={reason} />)}
          {r.verdict === 'already_synced' && (
            <Text type="secondary">
              {r.summary?.identical_release
                ? <>同大版本下已有清单完全相同的主版本 <Text code>{r.summary.identical_release}</Text>，不会生成新主版本。</>
                : <>基版{r.summary?.base_release ? ` ${r.summary.base_release} ` : ''}已包含这些版本，不会生成新主版本。</>}
            </Text>
          )}
          {r.warnings.length > 0 && (
            <details className="msync-warnings" open={canSync(r.verdict) || undefined}>
              <summary>{r.warnings.length} 条提示</summary>
              <ul>{r.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </details>
          )}
        </div>
      ),
    },
    {
      title: <Tooltip title="默认自动生成，可改；只能用字母、数字、点、下划线、连字符，不能与已有主版本重名">主版本号</Tooltip>, width: 280,
      render: (_, r) => {
        if (canSync(r.verdict) && r.summary?.new_release_name) {
          const err = selected.includes(r.ndpVersion) ? nameErrors[r.ndpVersion] : undefined;
          return (
            <div>
              <Input
                size="small" maxLength={64} status={err ? 'error' : undefined} value={releaseNames[r.ndpVersion] ?? ''}
                placeholder={r.summary.new_release_name}
                onChange={(e) => setReleaseNames((m) => ({ ...m, [r.ndpVersion]: e.target.value }))}
                style={{ fontFamily: 'ui-monospace, monospace' }}
              />
              {err && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 2 }}>{err}</div>}
            </div>
          );
        }
        if (r.verdict === 'already_synced') {
          const reuse = r.summary?.identical_release ?? r.summary?.base_release;
          if (reuse) return <Text type="secondary">沿用 {reuse}</Text>;
        }
        return <Text type="secondary">不生成</Text>;
      },
    },
  ];

  return (
    <Modal
      open={open} onCancel={onClose} width={1180} destroyOnHidden maskClosable={false}
      title={<Space><CloudSyncOutlined />新建模型同步{retryOf ? <Tag>重试 #{retryOf}</Tag> : null}</Space>}
      footer={step === 0 ? [
        <Button key="cancel" onClick={onClose}>取消</Button>,
        <Button key="preview" type="primary" icon={<EyeOutlined />} loading={previewing}
          disabled={parsed.versions.length === 0 || parsed.versions.length > PREVIEW_MAX_VERSIONS}
          onClick={doPreview}>预览 {parsed.versions.length || ''} 个发版号</Button>,
      ] : [
        <Button key="back" onClick={() => setStep(0)} disabled={creating}>上一步</Button>,
        <Button key="create" type="primary" icon={<CloudSyncOutlined />} loading={creating} disabled={selected.length === 0 || Object.keys(nameErrors).length > 0 || Object.keys(skipErrors).length > 0}
          onClick={doCreate}>确认同步 {selected.length} 个发版号</Button>,
      ]}
    >
      <Steps size="small" current={step} style={{ marginBottom: 20 }} items={[{ title: '输入发版号、选基版' }, { title: '预览并确认' }]} />
      {step === 0 ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Select
            showSearch allowClear placeholder="从数据中台发版列表搜索添加（版本号 / 名称 / 描述）" style={{ width: '100%' }}
            loading={releasesLoading} filterOption={false} value={null}
            searchValue={releaseKw} onSearch={setReleaseKw} autoClearSearchValue={false}
            notFoundContent={releasesLoading ? '正在读取数据中台发版列表…' : releasesError
              ? <Text type="danger">读取数据中台发版列表失败：{releasesError}</Text>
              : releaseKw.trim() ? `没有包含「${releaseKw.trim()}」的发版（匹配版本号 / 名称 / 描述，不区分大小写）` : '数据中台暂无发版'}
            dropdownRender={(menu) => (
              <>
                {menu}
                {!releasesLoading && !releasesError && releases.length >= NDP_RELEASE_LIMIT && (
                  <div style={{ padding: '6px 12px', borderTop: '1px solid #f0f0f0', fontSize: 12, color: '#8a8f99' }}>
                    {releaseKw.trim()
                      ? `匹配较多，只显示版本号最新的 ${NDP_RELEASE_LIMIT} 条，请输入更完整的关键字`
                      : `只显示最新的 ${NDP_RELEASE_LIMIT} 个发版；更早的请输入版本号 / 名称 / 描述搜索（不区分大小写）`}
                  </div>
                )}
              </>
            )}
            onSelect={(v) => {
              if (!v) {
                return;
              }
              if (parsed.versions.includes(v)) {
                message.info(`${v} 已在下方列表里`);
                return;
              }
              setText((t) => (t.trim() ? `${t.trim()}\n${v}` : v));
            }}
            options={releases.map((r) => ({
              value: r.ndpVersion,
              label: (
                <Space>
                  <Text strong>{r.ndpVersion}</Text>
                  {r.releaseKind && <Tag color={r.releaseKind === 'dev' ? 'orange' : 'blue'}>{r.releaseKind}</Tag>}
                  <span>{r.name}</span>
                  <Text type="secondary">{r.modelCount} 个模型</Text>
                  {r.syncedRelease ? <Tag color="green">已同步 {r.syncedRelease}</Tag> : <Tag>未同步</Tag>}
                  {parsed.versions.includes(r.ndpVersion) && <Tag color="purple">已添加</Tag>}
                </Space>
              ),
            }))}
          />
          <Input.TextArea
            value={text} onChange={(e) => setText(e.target.value)} autoSize={{ minRows: 6, maxRows: 14 }}
            placeholder={'每行一个中台发版号（14 位数字），也可用逗号分隔，例如：\n20261011204444\n20260731150416'}
          />
          <div>
            <Text type="secondary">识别到 {parsed.versions.length} 个发版号，单次最多 {PREVIEW_MAX_VERSIONS} 个。</Text>
            {parsed.invalid.length > 0 && <div><Text type="danger">格式不对（应为 14 位数字）：{parsed.invalid.join('、')}</Text></div>}
            {parsed.duplicates.length > 0 && <div><Text type="warning">重复已合并：{parsed.duplicates.join('、')}</Text></div>}
          </div>
          <Space wrap style={{ width: '100%' }}>
            <Text>基版</Text>
            <Select
              showSearch allowClear style={{ width: 520 }} loading={baseLoading} value={base ?? undefined}
              placeholder="不选 = 最新正式主版本" optionFilterProp="label" options={baseOptions}
              onChange={(v) => setBase(v ?? null)}
            />
            <Text type="secondary">默认不使用基版；选了基版时新主版本 = 基版 + 中台本次改动（同目录同名文件内容不同会拦下）</Text>
          </Space>
          {base === NO_BASE && (
            <Alert
              type="warning" showIcon
              message="不使用基版：新主版本只包含中台本次同步的模型"
              description="不含预测等其它模型类别，不可同步 / 取消勾选的模型类别也不在新主版本里；用到这些模型的仿真任务（如 planning_perception）会失败，跑 perception / tld 这类单模型任务不受影响。"
            />
          )}
          <Space wrap style={{ width: '100%' }}>
            <Text>同步 engine</Text>
            <Select
              mode="multiple" allowClear style={{ width: 520 }} value={engineModels} options={engineOptions}
              placeholder="不选 = 不同步 engine（默认）" onChange={setEngineModels}
            />
            <Text type="secondary">按模型类别开启，只同步中台的 GPU engine</Text>
          </Space>
          {engineModels.length > 0 && (
            <Alert
              type="warning" showIcon
              message="同步的 engine 必须是为仿真平台编译的（x86 GPU、TensorRT 版本一致），否则仿真加载会失败"
              description={(
                <div>
                  <div>· 只同步 GPU engine；DLA engine 是车端 Orin 专用，一律跳过。</div>
                  <div>· engine 须按 <Text code>{'{onnx名}_GPU_Trt{版本}_{精度}.engine'}</Text> 命名，且同一条中台记录里有对应的 onnx，否则该模型类别不可同步（其它模型类别照常）。</div>
                  <div>· 不同步 engine 时，每个仿真任务都会用 onnx 现场编译 engine，约多 18～24 分钟。</div>
                </div>
              )}
            />
          )}
          <Alert type="info" showIcon message="预览只做检查，不会写入任何数据。中台固定读取 pro 环境；未开启 engine 同步的模型类别不同步 engine。" />
        </Space>
      ) : preview && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Tag color="success">可同步 {counts.syncable}</Tag>
            {counts.partial > 0 && (
              <Tooltip title="部分模型类别不可同步：只同步可同步的；不可同步的不进本次改动（有基版时沿用基版的版本，不使用基版时不在新主版本里）">
                <Tag color="warning">部分可同步 {counts.partial}</Tag>
              </Tooltip>
            )}
            <Tag color="warning">无需同步 {counts.already_synced}</Tag>
            <Tag color="error">不可同步 {counts.blocked}</Tag>
            <Text type="secondary">预览结果 {Math.round(preview.expiresIn / 60)} 分钟内有效</Text>
          </Space>
          <Table<PreviewItem>
            size="small" rowKey="ndpVersion" pagination={false} dataSource={preview.items} columns={previewColumns}
            tableLayout="fixed" className="msync-wrap"
            rowSelection={{
              selectedRowKeys: selected,
              onChange: (keys) => setSelected(keys as string[]),
              getCheckboxProps: (r) => ({ disabled: !canSync(r.verdict) }),
            }}
            expandable={{
              rowExpandable: (r) => !!r.summary,
              // 发版号不多时默认展开明细，少一次点击
              defaultExpandedRowKeys: preview.items.length <= 3 ? preview.items.filter((i) => i.summary).map((i) => i.ndpVersion) : [],
              expandedRowRender: (r) => (
                <SummaryView
                  summary={r.summary}
                  removal={canSync(r.verdict) ? {
                    picked: removalPicks[r.ndpVersion] ?? [],
                    onChange: (names) => setRemovalPicks((p) => ({ ...p, [r.ndpVersion]: names })),
                  } : undefined}
                  skip={canSync(r.verdict) ? {
                    picked: skipPicks[r.ndpVersion] ?? [],
                    onChange: (names) => setSkipPicks((p) => ({ ...p, [r.ndpVersion]: names })),
                    error: selected.includes(r.ndpVersion) ? skipErrors[r.ndpVersion] : undefined,
                  } : undefined}
                />
              ),
            }}
          />
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={128} placeholder="任务名称（可选，默认 ndp-sync-时间）" />
        </Space>
      )}
    </Modal>
  );
}

/* ================================================================== *
 * 任务详情抽屉
 * ================================================================== */

function TaskDetailDrawer({ taskId, onClose, onRetry, onChanged }: {
  taskId: number | null; onClose: () => void; onChanged: () => void;
  onRetry: (versions: string[], retryOf: number, base: string, engineModels: string[]) => void;
}) {
  const { message } = App.useApp();
  const [detail, setDetail] = useState<SyncTaskDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [fileItem, setFileItem] = useState<SyncItem | null>(null);
  const [files, setFiles] = useState<SyncFile[] | null>(null);

  // 切换 / 关闭任务后，晚到的旧请求不能覆盖当前详情
  const detailSeq = useRef(0);
  const load = useCallback(async (silent = false) => {
    const seq = ++detailSeq.current;
    if (taskId === null) return;
    if (!silent) setLoading(true);
    try {
      const d = await getSyncTask(taskId);
      if (seq === detailSeq.current) setDetail(d);
    } catch (e) {
      // 请求层已提示；后台轮询失败只记录，下一轮再取
      console.error('加载同步任务详情失败', e);
    } finally {
      if (!silent && seq === detailSeq.current) setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    setDetail(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!detail || !TASK_ACTIVE.has(detail.status)) return undefined;
    const timer = window.setInterval(() => { void load(true); }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [detail, load]);

  const openFiles = async (item: SyncItem) => {
    setFileItem(item);
    setFiles(null);
    try {
      setFiles(await getSyncItemFiles(item.id));
    } catch {
      // 请求层已弹出错误提示；这里退回空列表，避免弹窗一直停在「加载中」
      setFiles([]);
    }
  };

  const doCancel = async () => {
    if (!detail) return;
    try {
      setDetail(await cancelSyncTask(detail.id));
      message.success('已取消；正在入库的发版号会继续完成');
      onChanged();
    } catch (e) {
      console.error('取消同步任务失败', e);  // 请求层已提示
    }
  };

  const failedVersions = detail?.items.filter((i) => i.status === 'failed' || i.status === 'cancelled').map((i) => i.ndpVersion) ?? [];

  const itemColumns: ColumnsType<SyncItem> = [
    {
      title: '发版号', dataIndex: 'ndpVersion', width: 200,
      render: (v, r) => (
        <div>
          <div><Text strong>{v}</Text>{r.releaseKind && <Tag style={{ marginLeft: 6 }} color={r.releaseKind === 'dev' ? 'orange' : 'blue'}>{r.releaseKind}</Tag>}</div>
          {r.ndpVersionName && <Text type="secondary" style={{ fontSize: 12 }}>{r.ndpVersionName}</Text>}
        </div>
      ),
    },
    {
      title: '状态', width: 140,
      render: (_, r) => (
        <div>
          <StatusTag map={ITEM_STATUS} status={r.status} />
          {r.stage && <div><Text type="secondary" style={{ fontSize: 12 }}>{STAGE_LABEL[r.stage] ?? r.stage}</Text></div>}
        </div>
      ),
    },
    {
      title: '进度', width: 160,
      render: (_, r) => (r.status === 'transferring' || r.status === 'pending'
        ? <Progress size="small" percent={progressPercent(r.progress)} format={() => `${r.progress?.done_files ?? 0}/${r.progress?.total_files ?? 0}`} />
        : '-'),
    },
    { title: '主版本', dataIndex: 'releaseName', width: 260, render: (v) => (v ? <Text code copyable>{v}</Text> : '-') },
    {
      title: '基版 / 移除', width: 220,
      render: (_, r) => (
        <div>
          <div>{r.baseRelease ? <Text code>{r.baseRelease}</Text> : <Text type="secondary">无基版</Text>}</div>
          {(r.removeModels ?? []).length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>移除：{(r.removeModels ?? []).join('、')}</Text>
          )}
        </div>
      ),
    },
    {
      title: '失败原因',
      render: (_, r) => (r.status === 'failed' ? (
        <div>
          <Tag color="error">{reasonLabel(r.failCode)}</Tag>
          <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }} ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}>
            {r.failReason}
          </Paragraph>
          <DetailList detail={r.failDetail} />
        </div>
      ) : '-'),
    },
    {
      title: '操作', width: 90,
      render: (_, r) => <Button size="small" type="link" icon={<FileTextOutlined />} onClick={() => void openFiles(r)}>文件</Button>,
    },
  ];

  return (
    <Drawer
      open={taskId !== null} onClose={onClose} width={1240} destroyOnHidden
      title={detail ? <Space>同步任务 #{detail.id}<StatusTag map={TASK_STATUS} status={detail.status} />{detail.name}</Space> : '同步任务'}
      extra={detail && (
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>刷新</Button>
          {failedVersions.length > 0 && !TASK_ACTIVE.has(detail.status) && (
            <Button
              icon={<RedoOutlined />}
              onClick={() => onRetry(
                failedVersions, detail.id,
                // 一个任务的子项共用一次预览的基版；沿用失败子项当时的基版和 engine 选择
                retryBaseValue(detail.items.find((i) => failedVersions.includes(i.ndpVersion))?.baseRelease),
                detail.engineModels ?? [],
              )}
            >重试失败的 {failedVersions.length} 个</Button>
          )}
          {TASK_ACTIVE.has(detail.status) && (
            <Popconfirm title="取消该同步任务？" description="排队中和搬运中的发版号会被取消；正在入库的发版号会继续完成。"
              okText="取消任务" cancelText="不取消" onConfirm={doCancel}>
              <Button danger icon={<StopOutlined />}>取消任务</Button>
            </Popconfirm>
          )}
        </Space>
      )}
    >
      {!detail ? <Empty description={loading ? '加载中…' : '无数据'} /> : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {detail.failReason && <Alert type="error" showIcon message={detail.failReason} />}
          <Descriptions size="small" column={3} items={[
            { key: 'c', label: '发起人', children: detail.creator },
            { key: 'e', label: '同步 engine', children: (detail.engineModels ?? []).length > 0 ? (detail.engineModels ?? []).join('、') : '不同步' },
            { key: 't', label: '创建时间', children: fmtTime(detail.createTime) },
            { key: 'd', label: '耗时', children: duration(detail.createTime, detail.finishTime) },
            { key: 'n', label: '结果', children: `成功 ${detail.succeededItems} · 无需同步 ${detail.alreadySyncedItems} · 失败 ${detail.failedItems} · 取消 ${detail.cancelledItems} · 共 ${detail.totalItems}` },
            { key: 'r', label: '重试自', children: detail.retryOf ? `#${detail.retryOf}` : '-' },
          ]} />
          <Table<SyncItem>
            size="small" rowKey="id" pagination={false} dataSource={detail.items} columns={itemColumns} className="msync-wrap"
            expandable={{
              rowExpandable: (r) => !!(r.result || r.previewSnapshot),
              expandedRowRender: (r) => (
                <div>
                  <SummaryView summary={r.result ?? r.previewSnapshot?.summary} />
                  {(r.result?.changes ?? []).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text strong>主版本改动</Text>
                      <ul className="msync-detail">
                        {(r.result?.changes ?? []).map((c, i) => (
                          <li key={i}>
                            {c.op === 'remove' ? <Tag color="red">移除</Tag> : c.op === 'add' ? <Tag color="green">新增</Tag> : <Tag color="blue">{c.op === 'replace' ? '替换' : c.op}</Tag>}
                            {c.name}
                            {(c.from || c.to) && <Text type="secondary" style={{ marginLeft: 6 }}>{c.from ?? '无'} → {c.to ?? '无'}</Text>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(r.result?.warnings ?? r.previewSnapshot?.warnings ?? []).length > 0 && (
                    <details className="msync-warnings" open>
                      <summary>提示</summary>
                      <ul>{(r.result?.warnings ?? r.previewSnapshot?.warnings ?? []).map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </details>
                  )}
                </div>
              ),
            }}
          />
        </Space>
      )}
      <Modal open={fileItem !== null} onCancel={() => setFileItem(null)} footer={null} width={1100}
        title={fileItem ? `${fileItem.ndpVersion} 的文件搬运情况` : ''}>
        <Table<SyncFile>
          size="small" rowKey="id" loading={files === null} className="msync-wrap" dataSource={files ?? []} pagination={{ pageSize: 20, hideOnSinglePage: true }}
          columns={[
            { title: '文件', dataIndex: 'fileName', render: (v, f) => <div><Text>{v}</Text>{f.modelName && <div><Text type="secondary" style={{ fontSize: 12 }}>{f.modelName}</Text></div>}</div> },
            { title: '大小', dataIndex: 'size', width: 100, render: (v) => (v == null ? '-' : formatBytes(v)) },
            { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={FILE_STATUS[v]?.color}>{FILE_STATUS[v]?.label ?? v}</Tag> },
            { title: '次数', dataIndex: 'attempts', width: 60 },
            {
              title: '说明',
              render: (_, f) => (f.errorCode ? (
                <div>
                  <Tag color={f.status === 'failed' ? 'error' : 'warning'}>{reasonLabel(f.errorCode)}</Tag>
                  <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }} ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}>{f.errorMessage}</Paragraph>
                </div>
              ) : <Text type="secondary" style={{ fontSize: 12 }}>{f.fetchJobId ?? ''}</Text>),
            },
          ]}
        />
      </Modal>
    </Drawer>
  );
}

/* ================================================================== *
 * 列表页
 * ================================================================== */

export default function ModelSync() {
  const [filters, setFilters] = useState<{ ndpVersion?: string; status?: string[]; creator?: string; releaseName?: string; range?: [Dayjs, Dayjs] | null }>({});
  const [query, setQuery] = useState<TaskListQuery>({ current: 1, pageSize: 20 });
  const [rows, setRows] = useState<SyncTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [retry, setRetry] = useState<{
    versions: string[]; retryOf?: number; base?: string; engineModels?: string[];
  }>({ versions: [] });
  const queryRef = useRef(query);
  queryRef.current = query;

  // 轮询请求晚于新查询返回时丢弃，避免表格回到旧筛选 / 旧页
  const listSeq = useRef(0);
  const load = useCallback(async (silent = false) => {
    const seq = ++listSeq.current;
    if (!silent) setLoading(true);
    try {
      const { items, page } = await listSyncTasks(queryRef.current);
      if (seq !== listSeq.current) return;
      setRows(items);
      setTotal(page?.totalElements ?? items.length);
    } catch (e) {
      console.error('加载同步任务列表失败', e);  // 请求层已提示
    } finally {
      if (!silent && seq === listSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [query, load]);

  useEffect(() => {
    if (!rows.some((r) => TASK_ACTIVE.has(r.status))) return undefined;
    const timer = window.setInterval(() => { void load(true); }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [rows, load]);

  const search = () => setQuery({
    current: 1, pageSize: query.pageSize,
    ndpVersion: filters.ndpVersion?.trim() || undefined,
    status: filters.status?.length ? filters.status : undefined,
    creator: filters.creator?.trim() || undefined,
    releaseName: filters.releaseName?.trim() || undefined,
    createTimeFrom: filters.range?.[0]?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
    createTimeTo: filters.range?.[1]?.add(1, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
  });

  const openCreate = (versions: string[] = [], retryOf?: number, base?: string, engineModels?: string[]) => {
    setRetry({ versions, retryOf, base, engineModels });
    setCreateOpen(true);
  };

  const columns: ColumnsType<SyncTask> = [
    { title: '任务', dataIndex: 'name', render: (v, r) => <a onClick={() => setDetailId(r.id)}>#{r.id} {v}</a> },
    { title: '发版号', dataIndex: 'ndpVersions', render: (vs: string[]) => <VersionTags versions={vs} /> },
    { title: '状态', dataIndex: 'status', width: 100, render: (v) => <StatusTag map={TASK_STATUS} status={v} /> },
    {
      title: '结果', width: 220,
      render: (_, r) => (
        <Space size={4} wrap>
          <Tag color="success">成功 {r.succeededItems}</Tag>
          {r.alreadySyncedItems > 0 && <Tag color="green">无需 {r.alreadySyncedItems}</Tag>}
          {r.failedItems > 0 && <Tag color="error">失败 {r.failedItems}</Tag>}
          {r.cancelledItems > 0 && <Tag>取消 {r.cancelledItems}</Tag>}
          <Text type="secondary">共 {r.totalItems}</Text>
        </Space>
      ),
    },
    { title: '发起人', dataIndex: 'creator', width: 200, ellipsis: true },
    { title: '创建时间', dataIndex: 'createTime', width: 170, render: fmtTime },
    { title: '耗时', width: 120, render: (_, r) => duration(r.createTime, r.finishTime) },
    { title: '操作', width: 80, render: (_, r) => <Button size="small" type="link" onClick={() => setDetailId(r.id)}>详情</Button> },
  ];

  return (
    <div className="msync">
      <Space wrap style={{ marginBottom: 16 }}>
        <Input allowClear placeholder="发版号" style={{ width: 180 }} value={filters.ndpVersion}
          onChange={(e) => setFilters((f) => ({ ...f, ndpVersion: e.target.value }))} onPressEnter={search} />
        <Select mode="multiple" allowClear placeholder="状态" style={{ minWidth: 180 }} value={filters.status}
          onChange={(status) => setFilters((f) => ({ ...f, status }))}
          options={Object.entries(TASK_STATUS).map(([value, s]) => ({ value, label: s.label }))} />
        <Input allowClear placeholder="发起人" style={{ width: 180 }} value={filters.creator}
          onChange={(e) => setFilters((f) => ({ ...f, creator: e.target.value }))} onPressEnter={search} />
        <Input allowClear placeholder="主版本名" style={{ width: 220 }} value={filters.releaseName}
          onChange={(e) => setFilters((f) => ({ ...f, releaseName: e.target.value }))} onPressEnter={search} />
        <DatePicker.RangePicker value={filters.range ?? null} onChange={(range) => setFilters((f) => ({ ...f, range: range as [Dayjs, Dayjs] | null }))} />
        <Button type="primary" icon={<SearchOutlined />} onClick={search}>查询</Button>
        <Button onClick={() => { setFilters({}); setQuery({ current: 1, pageSize: query.pageSize }); }}>重置</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>新建同步</Button>
      </Space>
      <Table<SyncTask>
        rowKey="id" loading={loading} dataSource={rows} columns={columns}
        locale={{ emptyText: <Empty description="还没有同步任务，点击「新建同步」输入中台发版号开始" /> }}
        pagination={{
          current: query.current, pageSize: query.pageSize, total, showSizeChanger: true,
          showTotal: (t) => `共 ${t} 个任务`,
          onChange: (current, pageSize) => setQuery((q) => ({ ...q, current, pageSize })),
        }}
      />
      <CreateSyncModal
        open={createOpen} initialVersions={retry.versions} retryOf={retry.retryOf}
        initialBase={retry.base} initialEngineModels={retry.engineModels}
        onClose={() => setCreateOpen(false)}
        onCreated={(task) => { setCreateOpen(false); void load(); setDetailId(task.id); }}
      />
      <TaskDetailDrawer
        taskId={detailId} onClose={() => setDetailId(null)} onChanged={() => void load(true)}
        onRetry={(versions, retryOf, base, engineModels) => {
          setDetailId(null);
          openCreate(versions, retryOf, base, engineModels);
        }}
      />
    </div>
  );
}
